<template>
  <div class="tour-point">
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mx-auto"></div>
      <p class="mt-4">{{ $t('loading', 'Loading...') }}</p>
    </div>
    
    <div v-else-if="pointData">
      <!-- Quiz Score Display -->
      <div v-if="quizScore" class="justify-between items-center p-4 text-center text-gray-800 mb-4">
        <div v-if="isDenHaagDomain">
          <img :src="`/static/thewalk/quiz/progress_${quizScore}.png`" class="w-36 m-auto" />
        </div>
        <div v-else>
          Score: {{ quizScore }}
        </div>
      </div>

      <!-- Main Content -->
      <div class="p-4 flow-root space-y-4 w-full">
        <div>

          <!-- Code Entry (if required) -->
          <CodeEntry 
            v-if="pointData?.code_required"
            :handleSubmit="handleSubmit"
            :handleChange="handleChange"
            :tourSettings="tourSettings"
          />
          
          <!-- Page Content -->
          <PageContent 
            v-else-if="activePage"
            :pageId="activePage"
            :tourId="activeTour"
            :form="form"
            :getQuizAnswer="getQuizAnswer"
            :handleChangeElement="handleChangeElement"
            :handleSubmit="handlePageSubmit"
            :isPageComplete="isCurrentPageComplete"
            :isPageLocked="isCurrentPageLocked"
            :debug="false"
            @page-content-loaded="handlePageContentLoaded"
          />
          
          <!-- Loading state for page content -->
          <div v-else class="text-center py-4">
            <p>Loading page content...</p>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <Pagination 
        v-if="filteredPages.length > 1 && !currentPageHasNavigation && getPageNumber(activePage) > 0"
        :filteredPages="filteredPages"
        :activePage="activePage"
        :tourSettings="tourSettings"
        :handleSubmitElements="handleSubmitElements"
        :isFirstPage="isFirstPage"
        :isLastPage="isLastPage"
        :getPageNumber="getPageNumber"
        :nextPage="nextPage"
        :previousPage="previousPage"
      />

    </div>
    
    <!-- Error State -->
    <div v-else class="text-center py-8">
      <p class="text-red-500">TourPoint: Point not found</p>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, h, markRaw } from 'vue'
import { useStore } from 'vuex'
import CodeEntry from '../components/page/CodeEntry.vue'
import PageContent from '../components/PageContent.vue'
import Pagination from '../components/page/Pagination.vue'
import { showMultiLingualText } from '../utils/languageUtils'
import api from '../services/api'
import i18n from '../i18n'
import { mapGetters } from 'vuex'
import moduleManager from '../modules/index.js'
import gameModule from '../modules/GameModule.js'

export default {
  name: 'TourPoint',
  components: {
    CodeEntry,
    PageContent,
    Pagination
  },
  props: {
    tourPointId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      pointData: null,
      activePage: '',
      hasQuiz: false,
      loading: true,
      availablePages: [], // Track available pages based on conditions
      pageContent: {}, // Store page content for each page
      currentMessage: '', // Store current message for form submission
      completedPages: new Set(), // Track which pages have been completed
      isExecutingPageActions: false, // Flag to prevent page condition re-evaluation during page action execution
      form: {
        code: '',
        hasQuiz: false,
        quiz_answers: {},
        variables: {}
      }
    }
  },
  computed: {
    ...mapGetters('settings', ['tourSettings']),
    ...mapGetters('tours', ['activeTour']),
    
    filteredPages() {
      console.log('=== FILTERED PAGES COMPUTED ===')
      console.log('availablePages:', this.availablePages)
      console.log('availablePages length:', this.availablePages.length)
      console.log('availablePages IDs:', this.availablePages.map(p => p.id))
      console.log('activePage:', this.activePage)
      console.log('currentPageHasNavigation:', this.currentPageHasNavigation)
      console.log('pointData exists:', !!this.pointData)
      console.log('pointData.pages exists:', !!this.pointData?.pages)
      console.log('pointData.pages length:', this.pointData?.pages?.length)
      console.log('pointData.pages IDs:', this.pointData?.pages?.map(p => p.id))
      
      // Ensure that if we have a valid activePage, it's always included in available pages
      let pages = [...this.availablePages]
      if (this.activePage && this.pointData?.pages) {
        const activePageExists = pages.find(p => p.id == this.activePage)
        if (!activePageExists) {
          const activePageData = this.pointData.pages.find(p => p.id == this.activePage)
          if (activePageData) {
            console.log('Adding active page to available pages:', this.activePage)
            pages.push(activePageData)
          }
        }
      }
      
      console.log('Final filtered pages:', pages.map(p => p.id))
      return pages
    },
    
    // Check if current page form is complete
    isCurrentPageComplete() {
      if (!this.activePage) return false
      
      const pageContent = this.pageContent[this.activePage]
      if (!pageContent) return false
      
      // Check if all required form fields are filled
      const blocks = pageContent.blocks || []
      
      for (const block of blocks) {
        if (block.type === 'form_radio_input') {
          const questionId = block.data?.id?.toString()
          if (questionId) {
            // Check if answer exists in local form state
            const hasLocalAnswer = this.form.quiz_answers && this.form.quiz_answers[questionId]
            
            // Check if answer exists in GameModule (persistent storage)
            const pointId = this.pointData?.point?.id
            const hasGameModuleAnswer = pointId ? gameModule.getQuizAnswer(pointId, questionId) : false
            
            if (!hasLocalAnswer && !hasGameModuleAnswer) {
              console.log(`Form incomplete: quiz question ${questionId} not answered (local: ${hasLocalAnswer}, gameModule: ${hasGameModuleAnswer})`)
              return false
            }
          }
        } else if (block.type === 'form_text_input') {
          const questionId = block.data?.id?.toString()
          if (questionId && block.data?.required) {
            // Check if answer exists in local form state
            const hasLocalAnswer = this.form.quiz_answers && this.form.quiz_answers[questionId] && this.form.quiz_answers[questionId].trim() !== ''
            
            // Check if answer exists in GameModule (persistent storage)
            const pointId = this.pointData?.point?.id
            const hasGameModuleAnswer = pointId ? gameModule.getQuizAnswer(pointId, questionId) : false
            
            if (!hasLocalAnswer && !hasGameModuleAnswer) {
              console.log(`Form incomplete: text question ${questionId} not answered (local: ${hasLocalAnswer}, gameModule: ${hasGameModuleAnswer})`)
              return false
            }
          }
        } else if (block.type === 'form_variable_input') {
          const varSlug = block.data?.variable_slug || block.data?.variable // fallback for backward compatibility
          if (varSlug && block.data?.required) {
            // Check if answer exists in GameModule (persistent storage)
            const hasGameModuleAnswer = gameModule.getVariableValue(varSlug) !== null
            
            if (!hasGameModuleAnswer) {
              console.log(`Form incomplete: required variable ${varSlug} not filled in GameModule`)
              return false
            }
          }
        } else if (block.type === 'game_image_spotter') {
          // Check if game has been completed
          const gameId = this.getGameId(block)
          console.log(`Form check: Game ${block.type} (${gameId}) form.game_answers:`, this.form.game_answers)
          const hasAnswer = this.form.game_answers && this.form.game_answers[gameId];

          console.log(`Form check: Game ${block.type} (${gameId}) hasAnswer: ${hasAnswer}`)
          if (!hasAnswer) {
            console.log(`Form incomplete: game ${block.type} not answered`)
            return false
          }
        } else if (this.isGameBlock(block.type)) {
          // Check if game has been completed
          const gameId = this.getGameId(block)
          const isGameCompleted = this.form.game_completions && this.form.game_completions[gameId] || gameModule.getGameCompletion(gameId) || false
          console.log(`Form check: Game ${block.type} (${gameId}) completed: ${isGameCompleted}`)
          if (!isGameCompleted) {
            console.log(`Form incomplete: game ${block.type} not completed`)
            return false
          }
        }
      }
      
      console.log(`Form complete for page ${this.activePage}`)
      return true
    },
    
    isDenHaagDomain() {
      return window.location.hostname === "denhaag-ctrl.tourio.com" || 
             window.location.hostname === "denhaag-ctrl.dev.tourio.com"
    },
    
    quizScore() {
      if (!this.pointData?.settings?.quiz?.percentage_complete) return null
      
      // This would need to be connected to the visited points store
      const score = 0 // Math.ceil(visitedPoints.value[this.pointData.point?.id]?.quiz_score || 0)
      
      if (this.isDenHaagDomain) {
        return Math.ceil(score / 10) * 10
      }
      
      return score
    },
    
    isQuizComplete() {
      // This would need to be connected to the visited points store
      return true // visitedPoints.value[this.pointData.point?.id]?.quiz_point_progress === 100
    },
    
    // Check if current page is locked
    isCurrentPageLocked() {
      return this.isPageLocked(this.activePage)
    },
    
    currentPageHasNavigation() {
      console.log('=== COMPUTING currentPageHasNavigation ===')
      console.log('activePage:', this.activePage)
      console.log('pointData:', this.pointData)
      
      if (!this.activePage || !this.pointData) {
        console.log('No activePage or pointData, returning false')
        return false
      }
      
      const currentPage = this.pointData.pages?.find(p => p.id == this.activePage)
      if (!currentPage) {
        console.log('No currentPage found, returning false')
        return false
      }
      
      // Check for page-level navigation rules
      const navigationRules = currentPage.navigation
      console.log('navigationRules:', navigationRules)
      const hasNavigationRules = navigationRules && (
        Array.isArray(navigationRules) ? navigationRules.length > 0 : Object.keys(navigationRules).length > 0
      )
      console.log('hasNavigationRules:', hasNavigationRules)
      
      if (hasNavigationRules) {
        console.log('Page has navigation rules, returning true')
        return true
      }
      
      // Check for form submit block navigation rules OR any submit button presence
      const pageContent = this.pageContent[this.activePage]
      console.log('pageContent:', pageContent)
      if (pageContent?.blocks) {
        console.log('pageContent.blocks:', pageContent.blocks)

        // Check for fallback submit button condition (same logic as PageContent)
        const hasFormFields = pageContent.blocks.some(block => 
          block.type && (block.type.startsWith('form_') || block.type.startsWith('game_')) && block.type !== 'form_submit_button'
        )
        console.log('hasFormFields:', hasFormFields)

        // Check for explicit submit button block
        const hasFormSubmitButton = pageContent.blocks.find(block => block.type === 'form_submit_button')
        console.log('hasFormSubmitButton:', hasFormSubmitButton)
        
        if (hasFormFields || hasFormSubmitButton) {
          console.log('Has form fields or submit button, returning true')
          return true
        }
      }
      
      console.log('No navigation found, returning false')
      return false
    },
  },
  watch: {
    tourPointId: {
      handler(newTourPointId, oldTourPointId) {
        console.log("tourPointId watcher triggered:", { newTourPointId, oldTourPointId })
        this.loadDataWithLanguage()
      },
      immediate: true
    },
    
    // Watch for active page changes to update overlay
    activePage: {
      handler(newActivePage, oldActivePage) {
        console.log('=== ACTIVE PAGE CHANGED ===')
        console.log('Old page:', oldActivePage)
        console.log('New page:', newActivePage)
        console.log('Available pages at change:', this.availablePages.map(p => p.id))
        console.log('Filtered pages at change:', this.filteredPages.map(p => p.id))
        
        
        if (newActivePage && this.pointData) {
          this.updateOverlayData()
          
          // Page actions are now handled by PageContent.vue when content is loaded
        }
      },
      immediate: false
    },
    
    // Watch for pointData changes to track page view
    pointData: {
      handler(newPointData) {
        if (newPointData && window._paq) {
          window._paq.push(['setCustomUrl', window.location.pathname])
          window._paq.push(['setDocumentTitle', this.showMultiLingualText(newPointData?.title, newPointData?.point?.name)])
          window._paq.push(['trackPageView'])
        }
      },
      immediate: false
    },
    
    // Watch for game state changes to refresh page filtering
    // Note: Game state is managed by module manager, not Vuex store
    // We'll use a different approach to detect changes
    
    // Watch for availablePages changes to debug pagination
    availablePages: {
      handler(newPages, oldPages) {
        console.log('=== AVAILABLE PAGES CHANGED ===')
        console.log('Old pages:', oldPages?.map(p => p.id))
        console.log('New pages:', newPages?.map(p => p.id))
        console.log('Old count:', oldPages?.length)
        console.log('New count:', newPages?.length)
      },
      deep: true
    },
    
    // Watch for page locking changes
    isCurrentPageLocked: {
      handler(newValue, oldValue) {
        console.log('=== CURRENT PAGE LOCKED CHANGED ===')
        console.log('Active page:', this.activePage)
        console.log('Old locked:', oldValue)
        console.log('New locked:', newValue)
        console.log('Is page completed:', this.isPageCompleted(this.activePage))
        console.log('Completed pages:', Array.from(this.completedPages))
      },
      immediate: true
    },
    
    // Watch for form changes
    form: {
      handler(newForm, oldForm) {
        console.log('=== FORM CHANGED ===')
        console.log('Old form:', oldForm)
        console.log('New form:', newForm)
        console.log('Quiz answers changed:', {
          old: oldForm?.quiz_answers,
          new: newForm?.quiz_answers
        })
      },
      deep: true,
      immediate: true
    }
  },
  beforeUnmount() {
    console.log('=== TOUR POINT BEFORE UNMOUNT ===')
    console.log('Component name:', this.$options.name)
    console.log('Tour point ID:', this.tourPointId)
    
    try {
      // Remove event listeners
      if (moduleManager) {
        moduleManager.removeEventListener('game:state_updated', this.refreshPageConditions.bind(this))
        moduleManager.removeEventListener('game:inventory_updated', this.refreshPageConditions.bind(this))
        moduleManager.removeEventListener('point:opening', this.handlePointOpening.bind(this))
      }
      
      // Remove inventory modal event listener
      if (window) {
        window.removeEventListener('show-inventory-modal', this.handleInventoryModalEvent.bind(this))
      }
      
      // Clear any pending timers or watchers
      if (this.gpsLockWatcher) {
        this.gpsLockWatcher()
        this.gpsLockWatcher = null
      }
      
      console.log('TourPoint component cleanup completed successfully')
    } catch (error) {
      console.error('Error during TourPoint beforeUnmount:', error)
    }
  },
  mounted() {
    console.log("=== TOUR POINT MOUNTED ===")
    console.log("Props at mount:", this.$props)
    console.log("tourPointId at mount:", this.tourPointId)
    console.log("Component name:", this.$options.name)
    
    // Test module manager
    console.log("Module manager test:")
    console.log("- Module manager exists:", !!moduleManager)
    console.log("- Game module active:", moduleManager.isModuleActive('game'))
    console.log("- Available modules:", moduleManager.getActiveModules())
    
    // Track page view only if pointData is available
    if (window._paq && this.pointData) {
      window._paq.push(['setCustomUrl', window.location.pathname])
      window._paq.push(['setDocumentTitle', this.showMultiLingualText(this.pointData?.title, this.pointData?.point?.name)])
      window._paq.push(['trackPageView'])
    }
    
    // Listen for inventory changes only; page variable changes are handled on submit per-page
    moduleManager.addEventListener('game:inventory_updated', this.refreshPageConditions.bind(this))
    
    // Listen for inventory change modal events
    window.addEventListener('show-inventory-modal', this.handleInventoryModalEvent.bind(this))
    
    // Listen for point opening events to handle actions
    moduleManager.addEventListener('point:opening', this.handlePointOpening.bind(this))
  },
  methods: {
    // Load data with correct language
    async loadDataWithLanguage() {
      console.log("=== LOAD DATA WITH LANGUAGE ===")
      console.log("tourPointId:", this.tourPointId)
      console.log("activeTour:", this.activeTour)
      
      if (!this.tourPointId) return
      
      this.loading = true
      try {
        
        // Get the detailed tour point data directly using the tour point ID
        console.log("Getting tour point details for ID:", this.tourPointId)
        const tourPointResponse = await this.$store.dispatch('tours/loadTourPoint', {
          tourPointId: this.tourPointId,
          activeTour: this.activeTour,
          language: this.$i18n.locale
        })
        
        console.log("Tour point API response:", tourPointResponse)
        
        if (tourPointResponse && tourPointResponse.data) {
          this.pointData = tourPointResponse.data
          console.log("Final point data:", this.pointData)
          console.log("Final point data keys:", Object.keys(this.pointData))
          console.log("Final point pages:", this.pointData.pages)
          console.log("Actions in point data:", this.pointData.actions)
          console.log("Conditions in point data:", this.pointData.conditions)
          
          // Check page conditions immediately after loading data
          console.log("Checking page conditions after loading data...")
          await this.checkPageConditions()
          
          // Proceed with normal flow (conditions already checked by event system)
          console.log("TourPoint data loaded, proceeding with normal flow")
          this.continueWithNormalFlow()
        } else {
          console.log("No tour point data returned from API")
          this.pointData = null
        }
        
      } catch (error) {
        console.error('Error loading tour point data:', error)
        this.pointData = null
      } finally {
        this.loading = false
      }
    },
    
    isLastPage(pageId) {
      return parseInt(pageId) === parseInt(this.filteredPages.at(-1)?.id)
    },
    
    isFirstPage(pageId) {
      return parseInt(pageId) === parseInt(this.filteredPages.at(0)?.id)
    },
    
    getPageNumber(pageId) {
      const index = this.filteredPages.findIndex(x => parseInt(x.id) === parseInt(pageId))
      return index + 1
    },
    
    nextPage(pageId) {
      const index = this.filteredPages.findIndex(x => parseInt(x.id) === parseInt(pageId))
      return this.filteredPages[index + 1]
    },
    
    previousPage(pageId) {
      const index = this.filteredPages.findIndex(x => parseInt(x.id) === parseInt(pageId))
      return this.filteredPages[index - 1]
    },
    
    getQuizAnswer(item) {
      if (item.type !== "form_radio_input" && item.type !== "form_text_input") return null
      
      // Get the question ID from the block data
      const questionId = item.data?.id?.toString()
      if (!questionId) return null
      
      // First check local form state (for current session)
      if (this.form.quiz_answers && this.form.quiz_answers[questionId]) {
        console.log('getQuizAnswer: found in local form state:', this.form.quiz_answers[questionId])
        return this.form.quiz_answers[questionId]
      }
      
      // Then check GameModule (for persistent storage)
      const pointId = this.pointData?.point?.id
      if (pointId) {
        const answer = gameModule.getQuizAnswer(pointId, questionId)
        if (answer) {
          console.log('getQuizAnswer: found in GameModule:', answer)
          // Also update local form state for consistency
          if (!this.form.quiz_answers) this.form.quiz_answers = {}
          this.form.quiz_answers[questionId] = answer
          return answer
        }
      }
      
      console.log('getQuizAnswer: no answer found')
      return null
    },
    
    isComplete() {
      const pointId = this.pointData.point?.id
      if (!pointId) return false
      
      // This would need to be connected to the visited points store
      return false // visitedPoints.value[pointId]?.quiz_point_progress === 100
    },
    
    handleChange(event) {
      this.form.code = event.target.value
    },
    
    handleChangeElement(event) {
      // Handle form element changes
      console.log('=== TOUR POINT HANDLE CHANGE ELEMENT ===')
      console.log('Event received:', event)
      console.log('Event target:', event.target)
      console.log('Event target name:', event.target?.name)
      console.log('Event target value:', event.target?.value)
      
      const { name, value } = event.target
      console.log('Form element changed:', { name, value })
      
      if (name.startsWith('answer_')) {
        // Handle quiz question answers
        const questionId = name.replace('answer_', '')
        if (!this.form.quiz_answers) this.form.quiz_answers = {}
        this.form.quiz_answers[questionId] = value
        console.log('Quiz answer updated:', { questionId, value, allAnswers: this.form.quiz_answers })
      } else if (name.startsWith('quiz_')) {
        // Legacy quiz answer format
        this.form.quiz_answers[name] = value
        console.log('Legacy quiz answer updated:', { name, value })
      } else if (name.startsWith('variable_')) {
        // Handle variable inputs - store directly in GameModule
        const varSlug = name.replace('variable_', '')
        this.form.variables[varSlug] = value;
        if (gameModule) {
          console.log('Variable should be updated in GameModule:', { varSlug, value })
        }
      } else {
        // Handle other form elements
        this.form[name] = value
        console.log('Other form element updated:', { name, value })
      }
      
      console.log('Current form state after update:', this.form)
      console.log('Form quiz_answers:', this.form.quiz_answers)
    },
    
    handleSubmit(event) {
      event.preventDefault()
      // Handle code submission
      this.loadDataWithLanguage()
    },
    
    async handleSubmitElements(event, nextPage, to = null) {
      event.preventDefault()
      
      // Handle form submission and navigation
      if (to) {
        this.$router.push(to)
      } else if (nextPage) {
        this.activePage = nextPage.id
      }
    },
    
    showMultiLingualText(text, altText = null) {
      return showMultiLingualText(text, altText, false, this.$i18n.locale)
    },
    
    updateOverlayData() {
      if (!this.pointData || !this.activePage) return
      
      // Get the current page from pointData
      const currentPage = this.pointData.pages?.find(p => p.id == this.activePage)
      
      if (currentPage) {
        // Update overlay data using Vuex store (without recreating component)
        this.$store.dispatch('overlay/updateOverlayData', {
          title: this.showMultiLingualText(this.pointData.title, '', true),
          subtitle: this.showMultiLingualText(currentPage.title, ''),
          image: this.pointData.image && this.tourSettings?.points_display_image ? this.pointData.image : null
        })
        
        console.log('Overlay data updated:', {
          title: this.showMultiLingualText(this.pointData.title, '', true),
          subtitle: this.showMultiLingualText(currentPage.title, ''),
          image: this.pointData.image
        })
      }
    },
    
    executeTourPointActions(pointData) {
      console.log('=== EXECUTE TOUR POINT ACTIONS ===')
      console.log('Tour point data:', pointData)
      console.log('Tour point ID:', this.tourPointId)
      
      // Check if GameModule is active
      if (!moduleManager.isModuleActive('game')) {
        console.log('GameModule is not active, skipping action execution')
        return
      }
      
      // Get actions from tour point data
      const actions = pointData.actions || []
      console.log('Tour point actions found:', actions)
      console.log('Actions length:', actions.length)
      
      if (actions.length === 0) {
        console.log('No actions found for this tour point')
        return
      }
      
      console.log('Executing tour point actions...')
      
      // Clear pending inventory changes
      // this.pendingInventoryChanges = [] // Removed as per edit hint
      
      // Execute each action based on trigger
      actions.forEach((action, index) => {
        console.log(`Checking tour point action ${index + 1}:`, action)
        
        // Create a unique key for this action to track if it's been executed
        const actionKey = `tour_point_${this.tourPointId}_action_${index}`
        console.log('Action key:', actionKey)
        console.log('Already executed:', gameModule.isActionExecuted(actionKey))
        console.log('executedActions:', gameModule.getExecutedActions())

        // Get trigger from the correct location in the action structure
        const trigger = action.trigger
        console.log('Action trigger:', trigger)
        
        // Check if action should be triggered
        let shouldExecute = false
        
        if (!trigger) {
          // No trigger specified - execute
          shouldExecute = true
          console.log('No trigger specified - will execute')
        } else if (trigger === 'every_time') {
          // Execute every time
          shouldExecute = true
          console.log('Trigger is every_time - will execute')
        } else if (trigger === 'first_time') {
          // Execute only the first time - check if already executed using GameModule
          shouldExecute = !gameModule.isActionExecuted(actionKey)
          console.log('Trigger is only the first time - should execute:', shouldExecute)
        }
        
        if (shouldExecute) {
          console.log(`Executing tour point action ${index + 1} (trigger: ${trigger || 'default'})`)
          
          if (typeof action === 'object' && action.target_type) {
            // API action structure
            if (action.target_type === 'variable') {
              this.executeVariableAction(action)
            } else if (action.target_type === 'object') {
              gameModule.executeObjectAction(action, this.pointData?.point?.id)
            } else if (action.target_type === 'task') {
              gameModule.executeTaskAction(action, this.pointData?.point?.id)
            } else if (action.target_type === 'connectbox_command') {
              gameModule.executeConnectboxCommandAction(action, this.pointData?.point?.id)
            } else {
              console.warn(`Unknown action type: ${action.target_type}`)
            }
          } else if (typeof action === 'string') {
            // Legacy string format
            //this.executeStringAction(action)
          } else {
            console.warn(`Unknown action format:`, action)
          }
          
          // Mark action as executed if it's a 'first_time' trigger using GameModule
          if (trigger === 'first_time') {
            gameModule.markActionExecuted(actionKey)
            console.log(`Marked tour point action ${index + 1} as executed`)
          }
        } else {
          console.log(`Skipping tour point action ${index + 1} (trigger: ${trigger})`)
        }
      })
      
      // Show batch inventory changes modal if there are any changes
      gameModule.showBatchInventoryChanges()
    },
    
    // Execute variable action (API format)
    executeVariableAction(action) {
      console.log('Executing variable action:', action)
      
      // Handle nested data structure from API
      const { operation, value, trigger, target } = action
      const variableSlug = target?.slug || target?.name || 'unknown' // fallback for backward compatibility
      const numericValue = parseFloat(value) || 0
      
      console.log(`Variable action: ${variableSlug} ${operation} ${numericValue}`)
      
      // Get current value using GameModule's method
      const currentValue = gameModule.getVariableValue(variableSlug)
      console.log(`Current ${variableSlug}: ${currentValue}`)
      
      // Execute operation
      let newValue = currentValue
      switch (operation) {
        case 'increase':
          newValue = currentValue + numericValue
          break
        case 'decrease':
          newValue = currentValue - numericValue
          break
        case 'multiply':
          newValue = currentValue * numericValue
          break
        case 'divide':
          newValue = currentValue / numericValue
          break
        case 'set':
          newValue = value
          break
        default:
          console.warn(`Unknown operation: ${operation}`)
          return
      }
      
      // Update the variable using GameModule's method
      gameModule.setVariableValue(variableSlug, newValue)
      console.log(`New ${variableSlug}: ${newValue}`)
      
      // Update module data to ensure other components see the change
      moduleManager.setModuleData('game', gameModule.getGameState())
    },
    
    // Execute string action (legacy format)
    executeStringAction(action) {
      console.log('Executing string action:', action)
      
      // Parse action (e.g., "score += 1", "level = 2", "custom_var = 10")
      const operators = ['+=', '-=', '*=', '/=', '=']
      let operator = null
      let variable = null
      let value = null
      
      // Find the operator
      for (const op of operators) {
        if (action.includes(op)) {
          operator = op
          const parts = action.split(op)
          variable = parts[0].trim()
          value = parts[1].trim()
          break
        }
      }
      
      if (!operator || !variable || value === null) {
        console.warn(`Invalid action format: ${action}`)
        return
      }
      
      const targetValue = parseFloat(value) || 0
      
      // Get current value using GameModule's method
      const currentValue = gameModule.getVariableValue(variable)
      
      console.log(`Action: ${variable} ${operator} ${value}`)
      console.log(`Before: ${currentValue}`)
      
      // Execute the action
      let newValue = currentValue
      switch (operator) {
        case '+=':
          newValue = currentValue + targetValue
          break
        case '-=':
          newValue = currentValue - targetValue
          break
        case '*=':
          newValue = currentValue * targetValue
          break
        case '/=':
          newValue = currentValue / targetValue
          break
        case '=':
          newValue = targetValue
          break
        default:
          console.warn(`Unknown operator: ${operator}`)
          return
      }
      
      // Update the variable using GameModule's method
      gameModule.setVariableValue(variable, newValue)
      console.log(`After: ${newValue}`)
      
      // Update module data to ensure other components see the change
      moduleManager.setModuleData('game', gameModule.getGameState())
    },
    
    continueWithNormalFlow() {
      console.log("=== CONTINUE WITH NORMAL FLOW ===")
      console.log("GameModule active:", moduleManager.isModuleActive('game'))
      console.log("Point data exists:", !!this.pointData)
      console.log("Point data actions:", this.pointData?.actions)
      
      // Execute tour point actions if GameModule is active
      if (moduleManager.isModuleActive('game')) {
        console.log("GameModule is active, executing tour point actions")
        this.executeTourPointActions(this.pointData)
      } else {
        console.log("GameModule is NOT active, skipping tour point actions")
      }
      
      // Check page conditions and update available pages
      this.checkPageConditions().then(() => {
        // Set first page as active if no page specified
        if (this.availablePages.length > 0 && !this.activePage) {
          this.activePage = this.availablePages[0].id
          console.log("Set active page to:", this.activePage)
        }
        
        // Check if quiz is required
        this.hasQuiz = !!this.pointData?.settings?.quiz
        
        // Update overlay data after point data is loaded
        this.$nextTick(() => {
          this.updateOverlayData()
        })
      })
    },
    
    // Check page conditions and update available pages
    async checkPageConditions() {
      console.log('=== CHECK PAGE CONDITIONS METHOD CALLED ===')
      console.log('pointData exists:', !!this.pointData)
      console.log('pointData.pages exists:', !!this.pointData?.pages)
      console.log('pointData.pages length:', this.pointData?.pages?.length)
      
      if (!this.pointData?.pages) {
        console.log('No pages found, setting availablePages to empty array')
        this.availablePages = []
        return
      }
      
      // Check if GameModule is active
      if (!moduleManager.isModuleActive('game')) {
        console.log('GameModule not active, all pages available')
        this.availablePages = this.pointData.pages
        return
      }
      
      console.log('GameModule is active, checking conditions for each page...')
      
      try {
        // Import GameModule to check conditions
        const { default: gameModule } = await import('../modules/GameModule.js')
        console.log('GameModule imported successfully')
        
        // Filter pages based on their conditions
        const availablePages = this.pointData.pages.filter(page => {
          console.log(`\n--- Checking page ${page.id} ---`)
          console.log('Page conditions:', page.conditions)
          console.log('Page conditions length:', page.conditions?.length)
          
          // If page has no conditions, it's always available
          if (!page.conditions || page.conditions.length === 0) {
            console.log(`Page ${page.id} has no conditions - available`)
            return true
          }
          
          // Check all conditions for this page
          const allConditionsMet = page.conditions.every(condition => {
            const isMet = gameModule.evaluateCondition(condition)
            console.log(`Page ${page.id} condition:`, condition, `- met: ${isMet}`)
            return isMet
          })
          
          console.log(`Page ${page.id} all conditions met: ${allConditionsMet}`)
          return allConditionsMet
        })
        
        console.log('\n=== FINAL FILTERING RESULTS ===')
        console.log('Original pages:', this.pointData.pages.map(p => p.id))
        console.log('Available pages after filtering:', availablePages.map(p => p.id))
        console.log('Original count:', this.pointData.pages.length)
        console.log('Available count:', availablePages.length)
        
        this.availablePages = availablePages
        console.log('Available pages after condition check:', availablePages.map(p => p.id))
        console.log('Available pages count:', availablePages.length)
        console.log('Original pages count:', this.pointData.pages.length)
        console.log('Original pages IDs:', this.pointData.pages.map(p => p.id))
        
        // If current active page is no longer available, switch to first available page
        if (this.activePage && !availablePages.find(p => p.id == this.activePage)) {
          console.log(`Current page ${this.activePage} no longer available, switching to first available page`)
          console.log('Available pages:', availablePages.map(p => p.id))
          console.log('Is executing page actions:', this.isExecutingPageActions)
          if (availablePages.length > 0) {
            this.activePage = availablePages[0].id
          } else {
            this.activePage = ''
          }
        }
        
      } catch (error) {
        console.error('Error checking page conditions:', error)
        // Fallback: show all pages if condition checking fails
        this.availablePages = this.pointData.pages
      }
    },
    
    refreshPageConditions() {
      console.log('=== REFRESH PAGE CONDITIONS ===')
      
      // Skip page condition re-evaluation if page actions are being executed
      // This prevents pages from disappearing after their own actions are executed
      if (this.isExecutingPageActions) {
        console.log('Skipping page condition refresh - page actions are being executed')
        return
      }
      
      this.checkPageConditions().then(() => {
        console.log('Page conditions refreshed')
      }).catch(error => {
        console.error('Error refreshing page conditions:', error)
      })
    },
    
    async handlePageSubmit(event) {
      console.log('=== FORM SUBMITTED IN TOUR POINT ===')
      console.log('Event:', event)
      
      // Extract form data from the event
      const formData = event.formData || event.target?.value || {}
      const message = event.message || ''
      console.log('Form data extracted:', formData)
      console.log('Message extracted:', message)

      // Set current message if provided
      if (message) {
        this.currentMessage = message
      }

      // Process the form submission (this handles conditional navigation)
      const navigationOccurred = await this.processFormSubmission(formData)
      
      // Debug current navigation state AFTER form processing
      this.debugNavigationState()
      
      // Only fall back to sequential navigation if no conditional navigation occurred
      if (navigationOccurred) {
        console.log('Conditional navigation occurred, skipping sequential navigation')
        return
      }
      
      // Fall back to sequential navigation only if no conditional navigation occurred
      console.log('No conditional navigation occurred, using sequential navigation')
      if (this.filteredPages.length > 1) {
        const currentIndex = this.filteredPages.findIndex(page => page.id == this.activePage)
        if (currentIndex >= 0 && currentIndex < this.filteredPages.length - 1) {
          const nextPage = this.filteredPages[currentIndex + 1]
          console.log('Navigating to next page:', nextPage.id)
          this.activePage = nextPage.id
        } else {
          console.log('No next page available, staying on current page')
        }
      } else {
        console.log('Only one page available, no navigation needed')
      }
    },
    
    handlePageContentLoaded(event) {
      console.log('=== PAGE CONTENT LOADED ===')
      console.log('Event:', event)
      
      const { pageId, content } = event
      console.log('Page ID:', pageId)
      console.log('Content:', content)
      
      // Store the page content
      this.pageContent[pageId] = content
      console.log('Stored page content for page:', pageId)
      console.log('All stored page content:', this.pageContent)
      
      // Set flag to prevent page condition re-evaluation during page action execution
      // This prevents the page from disappearing after its actions are executed
      this.isExecutingPageActions = true
      
      // Re-enable page condition re-evaluation after a short delay
      // This allows the page to stay visible even if its actions change game state
      setTimeout(() => {
        this.isExecutingPageActions = false
        console.log('Page action execution completed, page condition re-evaluation re-enabled')
      }, 2000) // 2 second delay to ensure the page stays visible
    },
    
    // Unified form processing method
    async processFormSubmission(formData) {
      console.log('=== UNIFIED FORM PROCESSING ===')
      console.log('Form data:', formData)
      
      // Check if GameModule is active
      if (!moduleManager.isModuleActive('game')) {
        console.log('GameModule is not active, skipping form processing')
        return false
      }
      
      // Get current page content
      const pageContent = this.pageContent[this.activePage]
      if (!pageContent) {
        console.log('No page content found for current page')
        return false
      }
      
      console.log('Processing form for page:', this.activePage)
      
      // Process all form fields and execute actions
      console.log('=== PROCESSING FORM FIELDS ===')
      const results = this.processFormFields(pageContent, formData)
      
      // Wait for Vue reactivity and store updates to complete
      await this.$nextTick()
      
      // Debug navigation state AFTER form fields are processed and variables are stored
      console.log('=== DEBUGGING NAVIGATION STATE AFTER FORM PROCESSING ===')
      this.debugNavigationState()
      
      // Check for conditional navigation
      const nextPage = this.determineNextPage(pageContent, results)
      
      // Mark current page as completed if navigation is successful OR if form is complete with no navigation rules
      if (nextPage) {
        console.log('Navigating to next page:', nextPage)
        console.log('Marking page as completed:', {
          activePage: this.activePage,
          activePageType: typeof this.activePage,
          beforeCompletedPages: Array.from(this.completedPages)
        })
        this.completedPages.add(this.activePage)
        console.log('After marking as completed:', {
          completedPages: Array.from(this.completedPages)
        })
        this.currentMessage = '' // Clear any previous message
        this.activePage = nextPage
        // Update availability only for the submitted page
        this.updateSinglePageAvailability(this.activePage)
        return true // Navigation occurred
      } else {
        console.log('No next page found, checking if form is complete...')
        
        // If form is complete but no navigation rules, mark as completed and stay on page
        if (this.isCurrentPageComplete) {
          console.log('Form is complete but no navigation rules - marking as completed')
          console.log('Marking page as completed:', {
            activePage: this.activePage,
            activePageType: typeof this.activePage,
            beforeCompletedPages: Array.from(this.completedPages)
          })
          this.completedPages.add(this.activePage)
          console.log('After marking as completed:', {
            completedPages: Array.from(this.completedPages)
          })
          this.currentMessage = 'Form submitted successfully!'
        } else {
          console.log('Form is incomplete - staying on page without marking as completed')
          // Don't mark as completed if form is incomplete
        }
        // Update availability only for the submitted page
        this.updateSinglePageAvailability(this.activePage)
        return false // No navigation occurred
      }
    },
    // Update availability for a single page without re-evaluating all pages
    updateSinglePageAvailability(pageId) {
      try {
        const page = (this.pointData && this.pointData.pages) ? this.pointData.pages.find(p => p.id === pageId) : null
        if (!page) return
        // If page has no conditions, it's always available
        let isAvailable = true
        if (page.conditions && page.conditions.length > 0) {
          isAvailable = page.conditions.every(condition => gameModule.evaluateCondition(condition))
        }

        const exists = this.availablePages.find(p => p.id === pageId)
        if (isAvailable && !exists) {
          // Add the page and keep original order based on pointData.pages
          const updated = [...this.availablePages, page]
          const order = (a, b) => {
            const ai = this.pointData.pages.findIndex(p => p.id === a.id)
            const bi = this.pointData.pages.findIndex(p => p.id === b.id)
            return ai - bi
          }
          this.availablePages = updated.sort(order)
        } else if (!isAvailable && exists) {
          // Remove the page from availablePages
          this.availablePages = this.availablePages.filter(p => p.id !== pageId)
        }
      } catch (e) {
        console.warn('updateSinglePageAvailability failed', e)
      }
    },
    
    // Process all form fields and execute actions
    processFormFields(pageContent, formData) {
      console.log('=== PROCESSING FORM FIELDS ===')
      const results = {
        actions: [],
        variables: {},
        answers: {}
      }
      
      // Process quiz answers
      if (formData.quiz_answers) {
        Object.entries(formData.quiz_answers).forEach(([questionId, answerId]) => {
          console.log(`Processing quiz answer: ${questionId} = ${answerId}`)
          
          // Store answer in GameModule
          const pointId = this.pointData?.point?.id
          if (pointId) {
            gameModule.setQuizAnswer(pointId, questionId, answerId)
          }
          
          // Find the quiz block (radio input)
          const radioBlock = pageContent.blocks?.find(block => 
            block.type === 'form_radio_input' && block.data?.id?.toString() === questionId
          )
          
          if (radioBlock) {
            // Find the selected option
            const selectedOption = radioBlock.data?.options?.find(option => 
              option.id.toString() === answerId.toString()
            )
            
            if (selectedOption) {
              console.log('Selected option:', selectedOption)
              
              // Store the answer
              results.answers[questionId] = {
                type: 'quiz',
                value: answerId,
                text: selectedOption.answer
              }
            }
          }
          
          // Find the text input block
          const textBlock = pageContent.blocks?.find(block => 
            block.type === 'form_text_input' && block.data?.id?.toString() === questionId
          )
          
          if (textBlock) {
            console.log('Text input answer:', answerId)
            
            // Store the answer
            results.answers[questionId] = {
              type: 'text',
              value: answerId,
              text: answerId
            }
          }
        })
      }

      // Store the answer of the image spotter based on form data game_answers
      if (formData.game_answers) {
        console.log(`Processing game answers: ${formData.game_answers}`)
        Object.entries(formData.game_answers).forEach(([gameId, answer]) => {
          console.log(`Processing game answer: ${gameId} = ${answer}`)
          results.answers[gameId] = {
            type: 'game',
            value: answer
          }
          if (answer === 'correct') {
            gameModule.setGameCompletion(gameId, true)
          }
        })
      }

      if (Object.keys(formData.variables).length > 0 && gameModule) {
        console.log('Updating variables in GameModule:', formData.variables)
        Object.entries(formData.variables).forEach(([varSlug, value]) => {
          gameModule.setVariableValue(varSlug, value)
          console.log('Variable updated in GameModule:', { varSlug, value })
        })

      }

      // Process input variables - variables are already stored in GameModule during input changes
      // Just build the results object for navigation evaluation
      const variableBlocks = pageContent.blocks?.filter(block => block.type === 'form_variable_input') || []
      variableBlocks.forEach(block => {
        const varSlug = block.data?.variable_slug || block.data?.variable // fallback for backward compatibility
        if (varSlug) {
          const value = gameModule.getVariableValue(varSlug)
          if (value !== null && value !== undefined) {
            results.variables[varSlug] = {
              type: 'input',
              value,
              label: block.data?.title || varSlug
            }
          }
        }
      })
      
      console.log('Form processing results:', results)
      return results
    },
    
    // Determine next page based on form results
    determineNextPage(pageContent, results) {
      console.log('=== DETERMINING NEXT PAGE ===')
      console.log('Page content:', pageContent)
      console.log('Results:', results)
      
      // Check if current page has navigation rules
      const navigationRules = pageContent.navigation
      const hasNavigationRules = navigationRules && (
        Array.isArray(navigationRules) ? navigationRules.length > 0 : Object.keys(navigationRules).length > 0
      )
      
      if (hasNavigationRules) {
        console.log('Using page-level navigation')
        console.log('Page navigation rules:', navigationRules)
        const nextPage = this.evaluatePageNavigation(navigationRules, results)
        console.log('Page-level navigation result:', nextPage)
        return nextPage
      }
      
      // Check if form submit block has navigation rules
      const formSubmitButton = pageContent.blocks?.find(block => block.type === 'form_submit_button')
      const buttonNavigationRules = formSubmitButton?.data?.navigation
      const hasButtonNavigationRules = buttonNavigationRules && (
        Array.isArray(buttonNavigationRules) ? buttonNavigationRules.length > 0 : Object.keys(buttonNavigationRules).length > 0
      )
      
      if (hasButtonNavigationRules) {
        console.log('Using button-level navigation')
        console.log('Button navigation rules:', buttonNavigationRules)
        const nextPage = this.evaluatePageNavigation(buttonNavigationRules, results)
        console.log('Button-level navigation result:', nextPage)
        return nextPage
      }
      
      console.log('No navigation rules found')
      return null
    },
    
    // Evaluate navigation rules
    evaluatePageNavigation(navigationRules, results) {
      // Convert to array if it's an object (ignore object keys, keep values)
      const rulesArray = Array.isArray(navigationRules) 
        ? navigationRules 
        : Object.values(navigationRules)
      
      // Check each navigation rule
      for (const rule of rulesArray) {
        console.log('Checking navigation rule:', rule)
        
        if (this.evaluateNavigationRule(rule, results)) {
          console.log('Navigation rule matched:', rule)
          
          // Execute actions if defined
          if (rule.actions && rule.actions.length > 0) {
            console.log('Executing navigation actions:', rule.actions.length)
            rule.actions.forEach((action, index) => {
              console.log(`Executing navigation action ${index + 1}:`, action)
              this.executeAction(action)
            })
          }
          
          // Handle navigation behavior
          if (rule.nextPage) {
            // Navigate to another page
            console.log('Navigating to page:', rule.nextPage)
            return rule.nextPage
          } else if (rule.close) {
            // Close the tour point and go back to points overview
            console.log('Closing tour point')
            
            // Close the overlay, this will change the url to points
            this.$store.dispatch('overlay/closeOverlay')

            return null
          } else if (rule.stay) {
            // Stay on current page (no navigation)
            console.log('Staying on current page')
            this.currentMessage = rule.message || 'Please check your input and try again.'
            return null
          } else {
            // Default: no navigation
            console.log('No navigation specified')
            return null
          }
        }
      }
      
      console.log('No navigation rules matched')
      return null
    },
    
    // Evaluate navigation rule
    evaluateNavigationRule(rule, results) {
      const { condition, nextPage } = rule
      
      if (!condition) return true // No condition means always navigate
      
      console.log('Evaluating navigation rule condition:', condition)
      
      // Check quiz answer conditions
      if (condition.form_answers) {
        console.log('Checking form_answers condition:', condition.form_answers)
        
        // Handle the API format: { answer_id: "70", question_id: "33" }
        if (condition.form_answers.answer_id && condition.form_answers.question_id) {
          const expectedAnswerId = condition.form_answers.answer_id
          const questionId = condition.form_answers.question_id
          
          // Get the actual answer from GameModule (submitted answers)
          const pointId = this.pointData?.point?.id
          if (!pointId) {
            console.log('No point ID available for navigation rule evaluation')
            return false
          }
          
          const actualAnswer = gameModule.getQuizAnswer(pointId, questionId)
          console.log(`Navigation rule check: question ${questionId}, expected answer: ${expectedAnswerId}, actual answer: ${actualAnswer}`)
          
          if (!actualAnswer || actualAnswer.toString() !== expectedAnswerId.toString()) {
            console.log(`Navigation rule failed: question ${questionId} answer mismatch`)
            return false
          }
          
          console.log(`Navigation rule passed: question ${questionId} answer matches`)
        }
      }
      
      // Check variable conditions
      if (condition.variables) {
        console.log('Checking variables condition:', condition.variables)
        
        // Handle the new API format: { value: "1", operator: "eq", variable: "punt_1_bezocht" }
        if (condition.variables.variable && condition.variables.value !== undefined && condition.variables.operator) {
          const varSlug = condition.variables.variable
          const expectedValue = condition.variables.value
          const operator = condition.variables.operator
          
          // Get the actual value from GameModule
          const actualValue = gameModule.getVariableValue(varSlug)
          console.log(gameModule.getAllVariables());
          console.log(`Navigation rule check: variable ${varSlug}, expected: ${expectedValue} (${operator}), actual: ${actualValue}`)
          
          // Evaluate based on operator
          const conditionMet = this.evaluateVariableCondition(actualValue, expectedValue, operator)
          
          if (!conditionMet) {
            console.log(`Navigation rule failed: variable ${varSlug} condition not met (${operator} ${expectedValue})`)
            return false
          }
          
          console.log(`Navigation rule passed: variable ${varSlug} condition met (${operator} ${expectedValue})`)
        }
      }
      
      // Check object/inventory conditions
      if (condition.objects) {
        console.log('Checking objects condition:', condition.objects)
        
        // Handle the API format: { value: 1, object: 2, operator: "eq" }
        if (condition.objects.object && condition.objects.operator && condition.objects.value !== undefined) {
          const objectId = condition.objects.object.toString()
          const operator = condition.objects.operator
          const expectedValue = condition.objects.value
          
          // Get the actual quantity from GameModule inventory
          const inventoryItem = gameModule.getInventoryItem(objectId);
          console.log(inventoryItem);
          const actualQuantity = inventoryItem ? inventoryItem.quantity : 0
          console.log(`Navigation rule check: object ${objectId}, expected: ${expectedValue} (${operator}), actual quantity: ${actualQuantity}`)
          
          // Evaluate based on operator
          const conditionMet = this.evaluateObjectCondition(actualQuantity, expectedValue, operator)
          
          if (!conditionMet) {
            console.log(`Navigation rule failed: object ${objectId} condition not met (${operator} ${expectedValue})`)
            return false
          }
          
          console.log(`Navigation rule passed: object ${objectId} condition met (${operator} ${expectedValue})`)
        }
      }
      
      // Check game completion conditions
      if (condition.games) {
        console.log('Checking games condition:', condition.games)

        // New format for image spotter correctness: { game_id: "412", answer: "correct"|"incorrect" }
        if (condition.games.game_id) {
          const gameId = condition.games.game_id
          const expectedAnswer = condition.games.answer // optional; only for image spotter

          if (expectedAnswer === 'correct' || expectedAnswer === 'incorrect') {
            // Use stored form answer from PageContent.handleGameComplete
            const actualAnswer = (this.form && this.form.game_answers && this.form.game_answers[gameId]) || null
            console.log(`Navigation rule check: image spotter ${gameId}, expected: ${expectedAnswer}, actual: ${actualAnswer}`)
            if (!actualAnswer || actualAnswer !== expectedAnswer) {
              console.log(`Navigation rule failed: image spotter ${gameId} answer mismatch`)
              return false
            }
            console.log(`Navigation rule passed: image spotter ${gameId} answer matches`)
          } else {
            // Check completion status when no expected answer is provided
            const expectedCompleted = true
            const actualCompleted = this.form.game_completions && this.form.game_completions[gameId] || false
            console.log(`Navigation rule check: game ${gameId}, expected completed: ${expectedCompleted}, actual completed: ${actualCompleted}`)
            if (actualCompleted !== expectedCompleted) {
              console.log(`Navigation rule failed: game ${gameId} completion status mismatch`)
              return false
            }
            console.log(`Navigation rule passed: game ${gameId} completion status matches`)
          }
        }
      }
      
      console.log('Navigation rule passed all conditions')
      return true
    },
    
    // Execute a single action
    executeAction(action) {
      console.log('Executing action:', action)
      
      // Create a unique key for this action to track if it's been executed
      const actionKey = `action_${Date.now()}_${Math.random()}`
      
      // Get trigger from the correct location in the action structure
      const actionData = action.data || action
      const trigger = actionData.trigger
      console.log('Action trigger:', trigger)
      
      // Check if action should be triggered
      let shouldExecute = false
      
      if (!trigger) {
        // No trigger specified - execute
        shouldExecute = true
        console.log('No trigger specified - will execute')
      } else if (trigger === 'every_time') {
        // Execute every time
        shouldExecute = true
        console.log('Trigger is every_time - will execute')
      } else if (trigger === 'first_time') {
        // Execute only the first time - check if already executed using GameModule
        shouldExecute = !gameModule.isActionExecuted(actionKey)
        console.log('Trigger is only the first time - should execute:', shouldExecute)
      }
      
      if (shouldExecute) {
        console.log(`Executing action (trigger: ${trigger || 'default'})`)
        
        if (typeof action === 'object' && action.target_type) {
          // API action structure
          if (action.target_type === 'variable') {
            this.executeVariableAction(action)
          } else if (action.target_type === 'object') {
            gameModule.executeObjectAction(action, this.pointData?.point?.id)
          } else if (action.target_type === 'task') {
            gameModule.executeTaskAction(action, this.pointData?.point?.id)
          } else {
            console.warn(`Unknown action type: ${action.target_type}`)
          }
        } else if (typeof action === 'string') {
          // Legacy string format
          this.executeStringAction(action)
        } else {
          console.warn(`Unknown action format:`, action)
        }
        
        // Mark action as executed if it's a 'first_time' trigger using GameModule
        if (trigger === 'first_time') {
          gameModule.markActionExecuted(actionKey)
          console.log(`Marked action as executed`)
        }
      } else {
        console.log(`Skipping action (trigger: ${trigger})`)
      }
    },
    
    // Check if a page has been completed (form submitted)
    isPageCompleted(pageId) {
      const completed = this.completedPages.has(pageId)
      const pointId = this.pointData?.point?.id
      
      // Check if this specific page has quiz answers in the GameModule
      let hasPageAnswers = false
      if (pointId && this.pageContent[pageId]) {
        const blocks = this.pageContent[pageId].blocks || []
        const quizBlocks = blocks.filter(block => block.type === 'form_radio_input')
        
        console.log(`isPageCompleted(${pageId}) - checking quiz blocks:`, {
          totalBlocks: blocks.length,
          quizBlocksCount: quizBlocks.length,
          quizBlockIds: quizBlocks.map(b => b.id),
          quizBlockDataIds: quizBlocks.map(b => b.data?.id)
        })
        
        hasPageAnswers = quizBlocks.some(block => {
          const questionId = block.data?.id?.toString()
          const answer = questionId ? gameModule.getQuizAnswer(pointId, questionId) : null
          
          console.log(`isPageCompleted(${pageId}) - checking question ${questionId}:`, {
            questionId,
            answer,
            hasAnswer: !!answer
          })
          
          return questionId && answer
        })
      }
      
      console.log(`isPageCompleted(${pageId}):`, {
        completed,
        pointId,
        hasPageAnswers,
        completedPages: Array.from(this.completedPages),
        gameModuleExists: !!gameModule,
        hasQuizAnswersMethod: typeof gameModule.hasQuizAnswers,
        hasQuizAnswersResult: pointId ? gameModule.hasQuizAnswers(pointId) : 'no pointId'
      })
      
      // A page is completed if it has been explicitly marked as completed in this session
      // OR if the page has quiz answers in the GameModule (indicating it was previously submitted)
      return completed || hasPageAnswers
    },
    
    // Check if a page is locked (completed and should show answers)
    isPageLocked(pageId) {
      console.log('isPageLocked called with:', {
        pageId,
        pageIdType: typeof pageId,
        activePage: this.activePage,
        activePageType: typeof this.activePage,
        completedPages: Array.from(this.completedPages),
        completedPagesTypes: Array.from(this.completedPages).map(id => typeof id)
      })

      return false;

      // TODO TO-231 this should be configurable to lock the page on submit
      
      const locked = this.isPageCompleted(pageId)
      console.log(`isPageLocked(${pageId}):`, locked)
      console.log(`isPageLocked(${pageId}) type:`, typeof locked)
      return Boolean(locked)
    },
    
    debugTogglePageLock() {
      console.log('=== DEBUG TOGGLE PAGE LOCK ===')
      console.log('Current state:', {
        activePage: this.activePage,
        activePageType: typeof this.activePage,
        completedPages: Array.from(this.completedPages),
        isPageLocked: this.isPageLocked(this.activePage),
        isPageCompleted: this.isPageCompleted(this.activePage)
      })
      
      if (this.completedPages.has(this.activePage)) {
        this.completedPages.delete(this.activePage)
        console.log('Page unlocked')
      } else {
        this.completedPages.add(this.activePage)
        console.log('Page locked')
      }
      
      console.log('New state:', {
        completedPages: Array.from(this.completedPages),
        isPageLocked: this.isPageLocked(this.activePage),
        isPageCompleted: this.isPageCompleted(this.activePage)
      })
    },
    
    debugShowState() {
      console.log('=== DEBUG SHOW STATE ===')
      console.log('Current state:', {
        activePage: this.activePage,
        activePageType: typeof this.activePage,
        completedPages: Array.from(this.completedPages),
        completedPagesTypes: Array.from(this.completedPages).map(id => typeof id),
        isPageLocked: this.isPageLocked(this.activePage),
        isPageCompleted: this.isPageCompleted(this.activePage),
        form: this.form
      })
    },
    
    debugNavigationState() {
      console.log('=== DEBUG NAVIGATION STATE ===')
      const pointId = this.pointData?.point?.id
      const currentPageContent = this.pageContent[this.activePage]
      
      console.log('Navigation debug info:', {
        pointId,
        activePage: this.activePage,
        hasPageContent: !!currentPageContent,
        pageNavigationRules: currentPageContent?.navigation || [],
        formSubmitButton: currentPageContent?.blocks?.find(b => b.type === 'form_submit_button'),
        formData: this.form,
        quizAnswers: this.form.quiz_answers || {},
        gameModuleActive: moduleManager.isModuleActive('game')
      })
      
      // Check specific quiz answers if they exist
      if (this.form.quiz_answers) {
        Object.entries(this.form.quiz_answers).forEach(([questionId, answerId]) => {
          const gameModuleAnswer = pointId ? gameModule.getQuizAnswer(pointId, questionId) : null
          console.log(`Quiz answer debug - Question ${questionId}:`, {
            formAnswer: answerId,
            gameModuleAnswer,
            match: answerId === gameModuleAnswer
          })
        })
      }
      
      // Check navigation rules for variables and show their current values
      if (currentPageContent?.navigation) {
        (Array.isArray(currentPageContent.navigation) 
          ? currentPageContent.navigation 
          : Object.values(currentPageContent.navigation)
        ).forEach((rule, index) => {
          if (rule.condition?.variables) {
            console.log(`Navigation rule ${index} variable conditions:`, rule.condition.variables)
            
            // Check new API format
            if (rule.condition.variables.variable) {
              const varSlug = rule.condition.variables.variable
              const expectedValue = rule.condition.variables.value
              const operator = rule.condition.variables.operator
              const actualValue = gameModule.getVariableValue(varSlug)
              
              console.log(`Variable condition debug - ${varSlug}:`, {
                expectedValue,
                operator,
                actualValue,
                conditionMet: this.evaluateVariableCondition(actualValue, expectedValue, operator)
              })
            }
            
            // Check legacy format
            Object.entries(rule.condition.variables).forEach(([varSlug, expectedValue]) => {
              if (varSlug !== 'variable' && varSlug !== 'value' && varSlug !== 'operator') {
                const actualValue = gameModule.getVariableValue(varSlug)
                console.log(`Legacy variable condition debug - ${varSlug}:`, {
                  expectedValue,
                  actualValue,
                  match: actualValue !== null && actualValue !== undefined && actualValue.toString() === expectedValue.toString()
                })
              }
            })
          }
          
          if (rule.condition?.objects) {
            console.log(`Navigation rule ${index} object conditions:`, rule.condition.objects)
            
            // Check API format: { value: 1, object: 2, operator: "eq" }
            if (rule.condition.objects.object && rule.condition.objects.operator && rule.condition.objects.value !== undefined) {
              const objectId = rule.condition.objects.object.toString()
              const expectedValue = rule.condition.objects.value
              const operator = rule.condition.objects.operator
              const inventoryItem = gameModule.getInventoryItem(objectId)
              const actualQuantity = inventoryItem ? inventoryItem.quantity : 0
              
              console.log(`Object condition debug - ${objectId}:`, {
                expectedValue,
                operator,
                actualQuantity,
                conditionMet: this.evaluateObjectCondition(actualQuantity, expectedValue, operator)
              })
            }
            
            // Check legacy format
            Object.entries(rule.condition.objects).forEach(([objectId, expectedQuantity]) => {
              if (objectId !== 'object' && objectId !== 'operator' && objectId !== 'value') {
                const inventoryItem = gameModule.getInventoryItem(objectId)
                const actualQuantity = inventoryItem ? inventoryItem.quantity : 0
                console.log(`Legacy object condition debug - ${objectId}:`, {
                  expectedQuantity,
                  actualQuantity,
                  match: actualQuantity >= expectedQuantity
                })
              }
            })
          }
        })
      }
    },
    
    // Helper method to evaluate variable conditions
    evaluateVariableCondition(actualValue, expectedValue, operator) {
      switch (operator) {
        case 'eq':
        case '==':
          return actualValue !== null && actualValue !== undefined && actualValue.toString() === expectedValue.toString()
        case 'ne':
        case 'neq':
        case '!=':
          return actualValue !== null && actualValue !== undefined && actualValue.toString() !== expectedValue.toString()
        case 'gt':
        case '>':
          return actualValue !== null && actualValue !== undefined && parseFloat(actualValue) > parseFloat(expectedValue)
        case 'gte':
        case '>=':
          return actualValue !== null && actualValue !== undefined && parseFloat(actualValue) >= parseFloat(expectedValue)
        case 'lt':
        case '<':
          return actualValue !== null && actualValue !== undefined && parseFloat(actualValue) < parseFloat(expectedValue)
        case 'lte':
        case '<=':
          return actualValue !== null && actualValue !== undefined && parseFloat(actualValue) <= parseFloat(expectedValue)
        default:
          return false
      }
    },

    // Helper method to evaluate object conditions
    evaluateObjectCondition(actualQuantity, expectedValue, operator) {
      switch (operator) {
        case 'eq':
        case '==':
          return actualQuantity === parseFloat(expectedValue)
        case 'ne':
        case 'neq':
        case '!=':
          return actualQuantity !== parseFloat(expectedValue)
        case 'gt':
        case '>':
          return actualQuantity > parseFloat(expectedValue)
        case 'gte':
        case '>=':
          return actualQuantity >= parseFloat(expectedValue)
        case 'lt':
        case '<':
          return actualQuantity < parseFloat(expectedValue)
        case 'lte':
        case '<=':
          return actualQuantity <= parseFloat(expectedValue)
        default:
          return false
      }
    },
    
    handleInventoryModalEvent(event) {
      console.log('=== INVENTORY MODAL EVENT ===')
      console.log('Event:', event)
      console.log('Event detail:', event.detail)
      
      const { title, message, type, data } = event.detail
      
      // Show the modal using Vuex store
      this.$store.dispatch('modal/openModal', {
        title,
        message,
        type,
        data
      })
      
      console.log('Inventory change modal opened via Vuex store')
    },
    
    handlePointOpening(event) {
      console.log('=== POINT OPENING EVENT ===')
      console.log('Event:', event)
      console.log('Event detail:', event.detail)
      
      const { pointId } = event.detail
      
      // Handle point opening logic
      this.loadDataWithLanguage()
    },
    
    getPointsUrl() {
      const currentLanguage = this.getCurrentLanguage()
      return `/${currentLanguage ? `${currentLanguage}/` : ''}points`
    },
    
    isGameBlock(blockType) {
      return typeof blockType === 'string' && blockType.startsWith('game_')
    },
    
    getGameId(block) {
      // Use block ID if available, otherwise use a combination of type and index
      return block.id || `${block.type}_${this.pageContent[this.activePage]?.blocks?.indexOf(block) || 0}`
    }
  }
}
</script>